import { isBefore } from 'date-fns';
import { isNil } from 'lodash-es';

import type {
  AssetsAssetSchemaTypedWithId,
  AssetsSegmentSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';
import type { FilesProxySchemaWithId } from '@iconik-io/iconik-api/src/services/files';
import type { Segment } from '@iconik-io/unified-player-core';

import type { SearchItemEntity } from '../../../../redux/entities/normalized/search_item';
import type { SegmentEntityWithIdNormalized } from '../../../../redux/entities/normalized/segment';
import { createUnifiedAnnotation } from '../../../../utils/annotation/create_unified_annotation';
import { pdfSegmentRangeThresholdDate } from '../segment/constants';

const MARKER_LANE_TYPE_COMMENT = 'comment';
const MARKER_LANE_TYPE_TIME_BASED_METADATA = 'time_based_metadata';
const MARKER_LANE_TYPE_TRANSCRIPTION = 'transcription';
const MARKER_LANE_TYPE_PERSON = 'person';

const COLOR_CODING = {
  acls: '#bd79fd',
  collections: '#8bc34a',
  comments: '#7ee1ff',
  discovery: '#7c4dff',
  face_recognition: '#a4e650',
  files: '#ffb11b',
  filters: '#f44336',
  history: '#9c27b0',
  metadata: '#07dab0',
  posters: '#e5e619',
  relations: '#97f5ff',
  reviews: '#fd8ef1',
  saved_searches: '#03a9f4',
  shares: '#f44336',
  subclips: '#ecb4b4',
  transcriptions: '#648df6',
  uploads: '#ffffff',
} as const;

const segmentTypeColorMap: {
  [type in AssetsSegmentSchemaWithId['segment_type']]?: string;
} = {
  COMMENT: COLOR_CODING.comments,
  PERSON: COLOR_CODING.face_recognition,
  TRANSCRIPTION: COLOR_CODING.transcriptions,
  GENERIC: COLOR_CODING.metadata,
  TAG: COLOR_CODING.metadata,
};

function getSegmentRange(
  segment: SegmentEntityWithIdNormalized,
  allSegments: SegmentEntityWithIdNormalized[],
  asset: AssetsAssetSchemaTypedWithId | SearchItemEntity | undefined,
) {
  const startMs = segment.time_start_milliseconds ?? undefined;
  const endMs = segment.time_end_milliseconds ?? undefined;
  const parent = allSegments.find(
    (potentialParent) => segment.parent_id === potentialParent.id,
  );
  if (parent) {
    return getSegmentRange(parent, allSegments, asset);
  }
  return { startMs, endMs };
}

const segmentTypeUnifiedTypeMap: {
  [type in AssetsSegmentSchemaWithId['segment_type']]?: string;
} = {
  COMMENT: MARKER_LANE_TYPE_COMMENT,
  PERSON: MARKER_LANE_TYPE_PERSON,
  TRANSCRIPTION: MARKER_LANE_TYPE_TRANSCRIPTION,
  GENERIC: MARKER_LANE_TYPE_TIME_BASED_METADATA,
  TAG: MARKER_LANE_TYPE_TIME_BASED_METADATA,
};

export const createUnifiedSegment = (
  segment: SegmentEntityWithIdNormalized,
  allSegments: SegmentEntityWithIdNormalized[],
  assetProxy: FilesProxySchemaWithId | undefined,
  asset: AssetsAssetSchemaTypedWithId | SearchItemEntity | undefined,
): Segment => {
  const color =
    segment.segment_color || segmentTypeColorMap[segment.segment_type];
  const { startMs, endMs } = getSegmentRange(segment, allSegments, asset);

  const segmentRangeDefined = !isNil(startMs) && !isNil(endMs);
  let range = segmentRangeDefined
    ? {
        start: startMs / 1000,
        end: endMs / 1000,
      }
    : undefined;

  // Beta PDF pages hack, see pdfSegmentRangeThresholdDate definition for more details.
  if (
    assetProxy?.format === 'PDF' &&
    range &&
    segmentRangeDefined &&
    startMs >= 1000 &&
    segment.date_created &&
    isBefore(new Date(segment.date_created), pdfSegmentRangeThresholdDate)
  ) {
    range = {
      start: (range.start - 1) / 1000,
      end: (range.end - 1) / 1000,
    };
  }

  const result: Segment = {
    id: segment.id,
    type: segmentTypeUnifiedTypeMap[segment.segment_type] ?? 'unknown',
    color,
    range,
  };

  if (segment.drawing) {
    result.annotation = createUnifiedAnnotation(
      segment.drawing,
      assetProxy?.resolution,
    );
  }

  return result;
};
